// src/modules/brands/contexts/BrandContext.tsx
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useBrandData } from "../hooks/useBrandData";
import { useBrandActions } from "../hooks/useBrandActions";
import {
  BrandDto,
  BrandHighlightDto,
  UserInfo,
  CreateBrandInput,
  UpdateBrandInput,
} from "../models/brand.model";

type BrandContextValue = {
  // List
  brands: BrandDto[];
  highlights: BrandHighlightDto[];
  userMap: Map<string, UserInfo>;
  loading: boolean;
  loaded: boolean;
  error: string | null;

  // Detail
  currentBrand: BrandDto | null;
  detailLoading: boolean;
  detailLoaded: boolean;
  detailError: string | null;
  loadBrandDetail: (id: string) => Promise<void>;

  getBrandById: (id: string) => BrandDto | undefined;
  actions: {
    refresh: () => Promise<void>;
    getUserInfo: (id: string) => UserInfo | undefined;
    searchUsers: (keyword: string) => Promise<UserInfo[]>;
    createBrand: (input: CreateBrandInput) => Promise<BrandDto>;
    updateBrand: (id: string, input: UpdateBrandInput) => Promise<BrandDto>;
    deleteBrand: (id: string) => Promise<boolean>;
    addBrandMember: (
      id: string,
      member: string,
      branchId: string,
    ) => Promise<BrandDto>;
    removeBrandMember: (
      id: string,
      member: string,
      branchId: string,
    ) => Promise<BrandDto>;
    uploadBrandLogo: (id: string, file: File) => Promise<BrandDto>;
    removeBrandLogo: (id: string, key: string) => Promise<BrandDto>;
    uploadBrandBanner: (id: string, file: File) => Promise<BrandDto>;
    removeBrandBanner: (id: string, key: string) => Promise<BrandDto>;
    addBrandMedia: (id: string, file: File) => Promise<BrandDto>;
    removeBrandMedia: (id: string, key: string) => Promise<BrandDto>;
    addHighlight: (brandId: string) => Promise<void>;
    removeHighlight: (brandId: string) => Promise<boolean>;
    reorderHighlight: (brandId: string, orderIndex: number) => Promise<void>;
    loadUserInfo: (id: string) => Promise<UserInfo | null>;
  };
};

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

export function BrandProvider({ children }: { children: ReactNode }) {
  const {
    brands,
    setBrands,
    highlights,
    setHighlights,
    userMap,
    loading,
    loaded,
    error,
    refresh,
    authArgs,
    service,
    loadUserInfo,
    currentBrand,
    detailLoading,
    detailLoaded,
    detailError,
    loadBrandDetail,
  } = useBrandData();

  const brandActions = useBrandActions(
    service,
    authArgs,
    setBrands,
    setHighlights,
    loadUserInfo,
  );

  const searchUsers = useCallback(
    async (keyword: string) => {
      const trimmed = keyword.trim();
      if (!trimmed) return [];
      return service.searchUsers(trimmed, authArgs);
    },
    [service, authArgs],
  );

  const value = useMemo<BrandContextValue>(
    () => ({
      brands,
      highlights,
      userMap,
      loading,
      loaded,
      error,
      currentBrand,
      detailLoading,
      detailLoaded,
      detailError,
      loadBrandDetail,
      getBrandById: (id) =>
        brands.find((b) => b.id === id) || currentBrand || undefined,
      actions: {
        refresh,
        getUserInfo: (id) => userMap.get(id),
        searchUsers,
        loadUserInfo,
        ...brandActions,
      },
    }),
    [
      brands,
      highlights,
      userMap,
      loading,
      loaded,
      error,
      currentBrand,
      detailLoading,
      detailLoaded,
      detailError,
      loadBrandDetail,
      refresh,
      searchUsers,
      loadUserInfo,
      brandActions,
    ],
  );

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context)
    throw new Error("useBrand phải được sử dụng trong BrandProvider");
  return context;
}
